// =============================================================================
// SELF-SET METRICS — funnel/KPI derivation for the admin dashboard
// =============================================================================
// Companion to `utils/selfSet.ts`, which owns the PREDICATE ("is this row self-set?").
// This file owns the AGGREGATION ("what do the self-set rows add up to?") for the
// Sankey-funnel page. Split deliberately: the Transactions page needs only the
// predicate, so importing it there stays free of dashboard metric weight, and the two
// pages can never disagree about which rows are self-set.
//
// Every formula below is the canonical one from `.claude/rules/transaction-context.md`
// + `constants/metricMeta.ts`. They are DELIBERATELY different denominators:
//   shows          = PIF + CPP + lost + rescheduled + pending_close   (R1)
//   decisiveCalls  = shows MINUS rescheduled = PIF + CPP + lost + pending_close  (R21/R24)
//   callsTaken     = shows + no_show
//   closeRate      = (PIF + CPP) / decisiveCalls        (R30 — what gates score on)
//   showUpRate     = shows / (shows + no_show)
// These denominators are DELIBERATELY different. Do not collapse them.
//
// ⚠ CORRECTED 2026-08-13. This block read `closeRate = (PIF + CPP) / shows` and ended
// with "Do not 'fix the inconsistency' — see the four-metric block in the rules file."
// Both were wrong after Mako's 2026-08-09 rulings, and the second was actively harmful:
// it told the next reader to REVERT the fix. The rules file itself now marks that
// four-metric block superseded. Denominator is decisiveCalls. See
// `.claude/rules/transaction-context.md` and `docs/handoff/[altara]_METRIC_RPC_SSOT.md`.
//
// WHY THIS COMPUTES CLIENT-SIDE INSTEAD OF ADDING AN RPC PARAM:
//   `TransactionContext` already streams every row this page needs to the browser
//   (5 event types, paginated past the PGRST 1000-row cap), and self-set is a ROW-LEVEL
//   predicate — no aggregation is needed to evaluate it, only to summarise it. Adding
//   `p_self_set_only` to `get_funnel_metrics` / `get_operations_kpis` would need a
//   DROP+CREATE migration (PostgREST resolves by arg count — an added overload makes
//   prod's existing 3-arg call ambiguous and breaks it) whose apply is Mako's trigger.
//   Shipping frontend ahead of that apply is exactly the P-OWNER half-ship that has been
//   rendering `0 setter · 0 dialer · 0 self` on this same page since 2026-07-04.
//   A zero-SQL filter cannot half-ship. See the handoff's Gotchas table.
// =============================================================================

import type { SalesTransaction } from "../types/core";
import { isSelfSet } from "./selfSet";
import { isDecisiveCall } from "./transactionStatus";

/** Statuses meaning the lead was PRESENT (the Sankey "Shown" stage).
 *  FIVE, not four — `pending_close` (show-not-win, shipped `6e764ca`, on prod) belongs
 *  here. Dropping it silently undercounts Shows. */
export const SHOWN_STATUSES = [
  "paid_in_full",
  "closed_payment_plan",
  "lost",
  "rescheduled",
  "pending_close",
] as const;

/** Statuses meaning the deal actually closed (the Sankey "Closed" stage).
 *  `pending_close` is deliberately ABSENT — the cash is still pending, so Shown counts
 *  it and Closed does not. Correct by design; never add it to win/cash lists. */
export const CLOSED_STATUSES = ["paid_in_full", "closed_payment_plan"] as const;

const SHOWN = new Set<string>(SHOWN_STATUSES);
const CLOSED = new Set<string>(CLOSED_STATUSES);

export interface SelfSetMetrics {
  /** Lead was present — the 5-status SHOWN allowlist. */
  shows: number;
  /** SCO rows with status `no_show`. */
  noShows: number;
  /** Closer hopped on the call: shows + noShows. */
  callsTaken: number;
  /** Deals closed — PIF + CPP only. */
  closed: number;
  /** The call reached a yes or a no: shows MINUS rescheduled (R21/R24).
   *  Counted from `DECISIVE_STATUSES`. This is the close-rate denominator. */
  decisiveCalls: number;
  /** closed ÷ decisiveCalls, as a percentage. 0 when there are no decisive calls.
   *  ⚠ Was `closed ÷ shows` until 2026-08-13. R21 removed rescheduled from the
   *  denominator, because a reschedule is attendance without a decision. */
  closeRate: number;
  /** shows ÷ (shows + noShows), as a percentage. 0 when the denominator is empty.
   *  Orphan pre-calls — the `+ orphan_precalls` term the canonical R14 rate carries —
   *  are structurally ZERO **for the self-set panel**, and the reason matters.
   *  `isSelfSet` gates on `event_type = 'sales_call_outcome'`; an orphan is a
   *  `sales_pre_call` with NO outcome row at all. The two sets are DISJOINT BY
   *  CONSTRUCTION, so no orphan can ever be self-set.
   *
   *  ⚠ The older wording said "a self-set deal has no pre-call behind it by
   *  definition." That reason is not quite right — measured live 2026-08-13, 11 of
   *  468 orphan pre-calls DO carry a null `setter_id`. Disjointness is the reason,
   *  not absence. Verified, not assumed.
   *
   *  ⚠⚠ THIS CLAIM DOES NOT TRANSFER TO `RevivedMetrics`, WHICH ALIASES THIS TYPE.
   *  Revived-ness is a property of the LEAD'S HISTORY, not of the call, so the
   *  disjointness argument does not hold there. Measured live 2026-08-13: 13 of 468
   *  orphan pre-calls fall inside a 30-day claim window, and all 13 are in the last
   *  90 days. See the note at `revivedMetrics.ts` `showUpRate` — it is an open RED. */
  showUpRate: number;
  /** Cash across ALL self-set outcome rows that carry it, not just wins — `paying_installment`
   *  is a STATUS on `sales_call_outcome`, so recurring payments are included, mirroring the
   *  portal's "all transactions with cash" attribution. */
  cash: number;
  /** Revenue closed across all self-set outcome rows. */
  revenue: number;
  /** Average cash per close. 0 when nothing closed. */
  avgCashPerClose: number;
  /** Self-set outcome rows in range — the `n=` receipt under the panel. */
  outcomeRows: number;
}

export const EMPTY_SELF_SET_METRICS: SelfSetMetrics = {
  shows: 0,
  noShows: 0,
  callsTaken: 0,
  closed: 0,
  decisiveCalls: 0,
  closeRate: 0,
  showUpRate: 0,
  cash: 0,
  revenue: 0,
  avgCashPerClose: 0,
  outcomeRows: 0,
};

/**
 * Aggregate the self-set slice of an already-date-filtered transaction list.
 *
 * `isSelfSet` gates on `event_type = 'sales_call_outcome'` as well as the null setter,
 * so non-call rows (low-ticket purchases, webinar signups, bookings) never leak in —
 * they carry a NULL setter by construction and would otherwise dwarf the real count.
 */
export function computeSelfSetMetrics(transactions: SalesTransaction[]): SelfSetMetrics {
  let shows = 0;
  let noShows = 0;
  let closed = 0;
  let decisiveCalls = 0;
  let cash = 0;
  let revenue = 0;
  let outcomeRows = 0;

  for (const t of transactions) {
    if (!isSelfSet(t)) continue;

    outcomeRows += 1;
    cash += t.cashCollected || 0;
    revenue += t.revenueClosed || 0;

    const status = t.callOutcome;
    if (SHOWN.has(status)) shows += 1;
    else if (status === "no_show") noShows += 1;
    if (CLOSED.has(status)) closed += 1;
    // decisiveCalls counted from the SSOT allowlist, NOT as `shows - rescheduled`.
    // Same population either way, but this cannot drift if SHOWN ever changes.
    if (isDecisiveCall(status)) decisiveCalls += 1;
  }

  const callsTaken = shows + noShows;
  return {
    shows,
    noShows,
    callsTaken,
    closed,
    decisiveCalls,
    closeRate: decisiveCalls > 0 ? (closed / decisiveCalls) * 100 : 0,
    showUpRate: callsTaken > 0 ? (shows / callsTaken) * 100 : 0,
    cash,
    revenue,
    avgCashPerClose: closed > 0 ? cash / closed : 0,
    outcomeRows,
  };
}
